import { Controller } from "@hotwired/stimulus"
import { destroy, post } from "@rails/request.js"

// Handles the saved views section in the support inbox sidebar:
// - Toggle the "save current view" inline form
// - Submit new saved view via fetch (JSON API)
// - Delete saved views via fetch
// data-controller="support--saved-views"
export default class extends Controller {
  static targets = ["form", "nameInput", "sharedCheckbox", "list", "emptyState"]
  static values = {
    createUrl: String,
    // CONEK-4296: kept for backwards compatibility, but the live filters are now
    // read from the URL at save time (the sidebar is no longer re-rendered on
    // filter changes, so a server-stamped snapshot would go stale).
    currentFilters: Object,
    // Sticky Board/List mode, used as a fallback when the URL has no view_mode
    // param yet (mode persisted only via cookie).
    viewMode: String,
  }

  // Reads the current filter combination from the URL: multi-value keys collapse
  // to a scalar when single, stay arrays when multiple. Pagination and the
  // saved-view context are excluded. The sticky view mode is always included.
  // Null when a saved view is applied but its resolved filters are not on the
  // page: saving the bare URL then would drop the view's own filters.
  currentFilters() {
    const params = this.#currentParams()
    if (!params) return null
    const skip = new Set(["page", "per_page", "saved_view_id"])
    const keys = new Set([...params.keys()].map((k) => k.replace(/\[\]$/, "")))
    const filters = {}
    keys.forEach((key) => {
      if (skip.has(key)) return
      const values = [
        ...params.getAll(`${key}[]`),
        ...params.getAll(key),
      ].filter(Boolean)
      if (values.length === 0) return
      filters[key] = values.length === 1 ? values[0] : values
    })
    if (!filters.view_mode && this.viewModeValue) {
      filters.view_mode = this.viewModeValue
    }
    return filters
  }

  // With a saved view applied the URL only carries its id; the filters it
  // resolved to are published in the ticket-content frame (CONEK-8035), so
  // saving from there captures them instead of failing "Apply a filter first".
  #currentParams() {
    const params = new URLSearchParams(window.location.search)
    if (!params.get("saved_view_id")) return params

    // Same rule as support--inbox-filters: without the published filters the
    // URL holds only the view's id and any refinement, never its stored
    // filters, so there is nothing safe to save.
    const published = document.querySelector("[data-support-effective-filters]")
    return published
      ? new URLSearchParams(published.dataset.supportEffectiveFilters || "")
      : null
  }

  toggleForm() {
    if (this.hasFormTarget) {
      this.formTarget.classList.toggle("hidden")
      if (
        !this.formTarget.classList.contains("hidden") &&
        this.hasNameInputTarget
      ) {
        this.nameInputTarget.focus()
      }
    }
  }

  hideForm() {
    if (this.hasFormTarget) {
      this.formTarget.classList.add("hidden")
      if (this.hasNameInputTarget) this.nameInputTarget.value = ""
      if (this.hasSharedCheckboxTarget)
        this.sharedCheckboxTarget.checked = false
    }
  }

  async save(event) {
    event.preventDefault()

    const name = this.hasNameInputTarget
      ? this.nameInputTarget.value.trim()
      : ""
    if (!name) return

    const shared = this.hasSharedCheckboxTarget
      ? this.sharedCheckboxTarget.checked
      : false

    // Capture the live filter combination from the URL. A bare view_mode is not
    // a real filter, so require at least one actual filter before saving.
    const filters = this.currentFilters()
    if (!filters) {
      this.#showInlineError(
        "Could not read the view's filters. Reload the page and try again.",
      )
      return
    }
    const hasFilter = Object.keys(filters).some((k) => k !== "view_mode")
    if (!hasFilter) {
      this.#showInlineError("Apply a filter first, then save the view.")
      return
    }

    // Get max position from existing views
    const existingViews = this.element.querySelectorAll("[data-saved-view-id]")
    const maxPosition = existingViews.length

    // NEVER send Content-Type: application/json — Rodauth treats that as
    // a JSON-API request and requires JWT Bearer auth, returning 401
    // "Please login to continue" even though the session cookie is valid.
    // FormData defaults Content-Type to multipart/form-data, which Rails
    // parses via the regular params parser.
    const formData = new FormData()
    formData.append("saved_view[name]", name)
    formData.append("saved_view[shared]", shared ? "1" : "0")
    formData.append("saved_view[position]", String(maxPosition))
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) =>
          formData.append(`saved_view[filter_params][${key}][]`, v),
        )
      } else {
        formData.append(`saved_view[filter_params][${key}]`, value)
      }
    })

    try {
      const response = await post(this.createUrlValue, {
        body: formData,
        responseKind: "json",
      })

      if (response.ok) {
        // Reload to show the new view in the sidebar
        window.location.reload()
      } else {
        const data = await response.json
        const errors = data.errors?.join(", ") || "Could not save view"
        this.#showInlineError(errors)
      }
    } catch (_error) {
      this.#showInlineError("Could not save view. Please try again.")
    }
  }

  #showInlineError(message) {
    // Remove any existing error
    this.element.querySelector("[data-save-error]")?.remove()
    const errEl = document.createElement("p")
    errEl.className = "text-sm text-destructive mt-1"
    errEl.setAttribute("data-save-error", "")
    errEl.textContent = message
    if (this.hasFormTarget) {
      this.formTarget.appendChild(errEl)
    }
  }

  async delete(event) {
    event.preventDefault()
    event.stopPropagation()

    // Capture the element NOW: after the first await, event.currentTarget is
    // reset to null by the browser, so closest() would no longer find the row
    // (the bug that left deleted views on screen until a reload).
    const button = event.currentTarget
    const url = button.dataset.deleteUrl
    if (!url) return

    try {
      const response = await destroy(url, { responseKind: "json" })

      if (response.ok || response.status === 204) {
        // Remove the view element from the DOM
        const viewEl = button.closest("[data-saved-view-id]")
        if (viewEl) {
          viewEl.remove()
          // Check if we need to show empty state
          const remaining = this.element.querySelectorAll(
            "[data-saved-view-id]",
          )
          if (remaining.length === 0 && this.hasEmptyStateTarget) {
            this.emptyStateTarget.classList.remove("hidden")
          }
        }
      }
    } catch (_error) {
      // Silently fail — the view will still be there on next page load
    }
  }
}
